import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { fetchVaihtoetuGradingForm, submitVaihtoetuPriceQuote } from '../../common/fetch.js';
import { t } from '../../common/i18n';
import { getPriceOrHyphen } from '../../common/utils/priceUtils.js';
import { VaihtoetuLead } from './VaihtoetuLead.js';
import type { Question } from '../../generated/api/question.js';
import type { VaihtoetuGradingForm } from '../../generated/api/vaihtoetuGradingForm.js';
import type { VaihtoetuPriceQuoteRequest } from '../../generated/api/vaihtoetuPriceQuoteRequest.js';
import type { VaihtoetuPriceQuoteResponse } from '../../generated/api/vaihtoetuPriceQuoteResponse.js';

interface FormProps {
  modelId: number;
}

type PriceQuoteAnswers = {
  answers: Record<number, number[]>;
};

const initPriceQuoteAnswers = (questions: Question[]): PriceQuoteAnswers => {
  const answers: Record<number, number[]> = {};
  questions.forEach(question => {
    answers[question.Id] = [];
  });
  return { answers };
};

const updateAnswer = (state: PriceQuoteAnswers, questionIndex: number, value: number): PriceQuoteAnswers => {
  return { answers: { ...state.answers, [questionIndex]: [value] } };
};

const allAnswersFilled = (state: PriceQuoteAnswers): boolean => {
  return Object.values(state.answers).every(answerArray => answerArray.length > 0);
};

export const VaihtoetuGrading = ({ modelId }: FormProps) => {
  const [gradingForm, setGradingForm] = useState<VaihtoetuGradingForm | null>(null);
  const [price, setPrice] = useState<number>(0);
  const [modelName, setModelName] = useState<string>('');
  const [currency, setCurrency] = useState<string>('€');
  const [answers, setAnswers] = useState<PriceQuoteAnswers>(initPriceQuoteAnswers([]));
  const [resetKey, setResetKey] = useState<number>(0);

  useEffect(() => {
    const loadGradingForm = async () => {
      if (!modelId) {
        return;
      }
      const response = await fetchVaihtoetuGradingForm(modelId);
      setGradingForm(response);
      setPrice(0);
      setAnswers(initPriceQuoteAnswers(response.Questions));
    };

    void loadGradingForm();
  }, [modelId, resetKey]);

  if (!modelId) {
    return null;
  }

  if (!gradingForm || !gradingForm.Questions || gradingForm.Questions.length === 0) {
    return null;
  }

  const priceQuote = async (newState: PriceQuoteAnswers) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const req: VaihtoetuPriceQuoteRequest = { ModelId: modelId, GradingForm: { Answers: newState.answers } };
    const response: VaihtoetuPriceQuoteResponse = await submitVaihtoetuPriceQuote(req);
    setPrice(response.OfferedPrice);
    setModelName(response.ModelName);
    setCurrency(response.CurrencySymbol);
  };

  const getQuestionsAndAnswers = () => {
    return gradingForm.Questions.map(question => ({
      question: question.Name,
      answer: question.Answers.find(answer => answers.answers[question.Id]?.includes(answer.Id))?.Name || '',
    }));
  };

  const handlePriceQuoteAnswer = (index: number, value: number) => {
    setAnswers(prev => {
      const newState = updateAnswer(prev, index, value);

      if (allAnswersFilled(newState)) {
        priceQuote(newState);
      }

      return newState;
    });
  };

  return (
    <div className={dsClass.MARGIN_TOP_5}>
      <h3 className={dsClass.H3_BOOK}>{t.ZYID('Assess the condition of your device')}</h3>
      <p>{t.B4DW('Answer questions about the condition of your device below')}</p>
      {gradingForm.Questions.map(question => (
        <div key={question.Id} className={dsClass.PADDING_0}>
          <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}>
            <h4 className={`${dsClass.H4} ${dsClass.MARGIN_RIGHT_3} ${dsClass.MARGIN_VERTICAL_0}`}>{question.Name}</h4>
          </div>
          <div className={dsClass.MARGIN_TOP_3}>
            <CL.RadioGroup key={`${modelId}-${resetKey}-${question.Id}`}>
              {question.Answers.map(answer => (
                <CL.RadioV2
                  key={answer.Id}
                  name={`question-${question.Id}`}
                  value={answer.Id.toString()}
                  label={answer.Name}
                  onChange={() => handlePriceQuoteAnswer(question.Id, answer.Id)}
                />
              ))}
            </CL.RadioGroup>
          </div>
        </div>
      ))}
      <div>
        <h3 className={dsClass.H3_BOOK}>{t.E9XD('Estimated price for your device')}</h3>
        <h2 className={`${dsClass.H2} of-vaihtoetu__price-offer`}>{getPriceOrHyphen(price, currency)}</h2>
        <p>
          {t.W6SC(
            'Please note that the final price will be confirmed only after we have received and inspected the device. Microdamage to glass components, hard to detect display defects, and any repairs made to the device may affect the final trade-in value.'
          )}
        </p>
      </div>
      <hr className={dsClass.MARGIN_VERTICAL_5} />
      <VaihtoetuLead
        modelName={modelName}
        answeredQuestions={getQuestionsAndAnswers()}
        offeredPrice={price}
        currency={currency}
        onLeadSubmitted={() => setResetKey(resetKey + 1)}
      />
    </div>
  );
};
